/**
 * <ErrorScreen> — what a person sees when a route fails outright.
 *
 * Every error the API returns carries a correlation id (the `X-Correlation-Id`
 * header the client reads onto the error). Showing it here, next to a copy
 * button, is the difference between "it broke" and a report support can trace
 * to the exact request in the server log.
 *
 * The id and the message are read defensively off the thrown value: this
 * pattern must not depend on the API client, and a route can throw anything.
 */
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../primitives/Button";
import { cn } from "../../utils";
import { usePageTitle } from "../PageTitle/PageTitle";

export interface ErrorScreenProps {
  /** Whatever the route threw — an ApiError, an Error, or anything else. */
  error?: unknown;
  /** Router-provided reset; falls back to a full reload when absent. */
  reset?: () => void;
  className?: string;
}

function readString(source: unknown, key: string): string | null {
  if (typeof source !== "object" || source === null) return null;
  const value = (source as Record<string, unknown>)[key];
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

export function ErrorScreen({ error, reset, className }: ErrorScreenProps): JSX.Element {
  const { t } = useTranslation("errors");
  const [copied, setCopied] = useState(false);
  // Inside the shell the heading names the page from the top bar.
  const bar = usePageTitle({ title: t("boundary.heading") });

  // `title` is the backend's pre-localized problem title; `message` covers a
  // plain Error; anything else falls back to the generic sentence.
  const title = readString(error, "title") ?? readString(error, "message") ?? t("generic.unknown");
  const correlationId = readString(error, "correlationId");

  const copyReference = (): void => {
    if (correlationId === null) return;
    void navigator.clipboard?.writeText(correlationId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      role="alert"
      className={cn(
        "flex min-h-[60vh] flex-col items-center justify-center gap-md px-lg text-center",
        className,
      )}
    >
      {bar.inShell ? (
        bar.titleNode
      ) : (
        <h1 className="text-heading text-default">{t("boundary.heading")}</h1>
      )}
      <p className="max-w-prose text-body text-muted">{t("boundary.body", { title })}</p>

      {correlationId !== null && (
        <div className="flex flex-wrap items-center justify-center gap-xs">
          <span className="text-label text-muted" style={{ fontFamily: "var(--font-family-mono)" }}>
            {t("boundary.reference", { correlationId })}
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={copyReference}>
            {copied ? t("boundary.copied") : t("boundary.copy_reference")}
          </Button>
        </div>
      )}

      <Button
        type="button"
        variant="primary"
        onClick={() => (reset !== undefined ? reset() : globalThis.location.reload())}
      >
        {t("boundary.retry")}
      </Button>
    </div>
  );
}
